import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { BulkEditSectionFormService, MultiSelect, ZagIcon, type DropdownOption } from '@zag/ui';
import type { ManagedGroup } from '@zag/auth/models';

const SECTION_ID = 'bulk-groups';

type Mode = 'add' | 'remove';

interface State {
  groupIds: string[];
  mode: Mode;
}

@Component({
  selector: 'sc-user-groups-bulk-section',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MultiSelect, TranslatePipe, ZagIcon],
  template: `
    <div class="sc-groups-bulk-section">
      <div class="sc-groups-bulk-section__mode-toggle">
        <button
          type="button"
          class="sc-groups-bulk-section__mode-option"
          [class.sc-groups-bulk-section__mode-option--active]="state().mode === 'add'"
          (click)="setMode('add')"
        >
          <span zagIcon="add"></span>
          <span>{{ 'bulkEdit.users.groups.modeAdd' | translate }}</span>
        </button>
        <button
          type="button"
          class="sc-groups-bulk-section__mode-option"
          [class.sc-groups-bulk-section__mode-option--active]="state().mode === 'remove'"
          (click)="setMode('remove')"
        >
          <span zagIcon="remove"></span>
          <span>{{ 'bulkEdit.users.groups.modeRemove' | translate }}</span>
        </button>
      </div>
      <zag-multi-select
        [options]="groupOptions()"
        [placeholder]="'bulkEdit.users.groups.selectGroups' | translate"
        [searchable]="true"
        [searchPlaceholder]="'bulkEdit.users.groups.searchGroups' | translate"
        [noResultsText]="'bulkEdit.users.groups.noGroups' | translate"
        [clearAriaLabel]="'bulkEdit.users.groups.clearGroups' | translate"
        [removeAriaLabel]="'bulkEdit.users.groups.removeGroup' | translate"
        [ngModel]="state().groupIds"
        (ngModelChange)="onGroupIdsChange($event)"
      />
    </div>
  `,
  styles: [
    `
      .sc-groups-bulk-section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        width: 100%;
      }

      .sc-groups-bulk-section__mode-toggle {
        display: flex;
        align-items: center;
        padding: var(--spacing-xs);
        background: var(--surface-color-secondary);
        border-radius: var(--border-radius-md);
      }

      .sc-groups-bulk-section__mode-option {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-sm);
        flex: 1;
        padding: var(--spacing-sm);
        background: transparent;
        border: none;
        border-radius: var(--border-radius-sm);
        color: var(--text-color-secondary);
        font-size: var(--font-size-sm);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--transition-duration-fast);

        span[zagIcon] {
          font-size: 18px;
        }

        &:hover {
          color: var(--text-color-primary);
        }

        &.sc-groups-bulk-section__mode-option--active {
          background: var(--background-color-elevated);
          color: var(--text-color-primary);
          box-shadow: var(--shadow-sm);
          cursor: default;
        }
      }
    `,
  ],
})
export class UserGroupsBulkSectionComponent implements OnInit, OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly formService = inject(BulkEditSectionFormService);

  protected readonly state = signal<State>({ groupIds: [], mode: 'add' });

  private readonly groupsResource = rxResource({
    stream: () => this.http.get<ManagedGroup[]>('/api/v1/rbac/groups'),
  });

  protected readonly groupOptions = computed<DropdownOption<string>[]>(() =>
    (this.groupsResource.value() ?? []).map(group => ({
      value: group.id,
      label: group.name,
    }))
  );

  protected setMode(mode: Mode): void {
    this.state.update(s => ({ ...s, mode }));
  }

  protected onGroupIdsChange(groupIds: string[]): void {
    this.state.update(s => ({ ...s, groupIds: [...groupIds] }));
  }

  ngOnInit(): void {
    this.formService.register(SECTION_ID, () => this.state());
  }

  ngOnDestroy(): void {
    this.formService.unregister(SECTION_ID);
  }
}
